import { Injectable } from '@angular/core';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngxs/store';
import { LicenseValidityChooser, LicenseValidityContext } from '@platform-frontend/marketplace';
import { ShoppingCartState } from '../shopping-cart.state';
import { ValidityConfigurationOption, ValidityMonthsFromNowOption } from './model/validity-configuration-option';

@UntilDestroy()
@Injectable()
export class ShoppingCartConfiguratorService {
  constructor(private store: Store) {}

  getValidityConfigurationOption(): ValidityConfigurationOption | null {
    const licenseTypeValidity: LicenseValidityContext | null = this.store.selectSnapshot(ShoppingCartState.licenseTypeValidity);
    const selectedValidity: LicenseValidityContext | null = this.store.selectSnapshot(ShoppingCartState.selectedValidity);
    const validityChooser: LicenseValidityChooser | null = this.store.selectSnapshot(ShoppingCartState.validityChooser);
    return this.validateToBeMonthsSelection(licenseTypeValidity, selectedValidity, validityChooser);
  }

  private validateToBeMonthsSelection(
    licenseTypeValidity: LicenseValidityContext | null,
    selectedValidity: LicenseValidityContext | null,
    validityChooser: LicenseValidityChooser | null
  ): ValidityMonthsFromNowOption | null {
    if (!validityChooser || !selectedValidity || !licenseTypeValidity) {
      return null;
    }
    if (validityChooser.durationConstraints?.endBeforeDate || validityChooser.durationConstraints?.endBeforeRelativeDate) {
      return null;
    }
    if (!selectedValidity.date || !validityChooser.dateConstraints) {
      return null;
    }
    if (
      validityChooser.dateConstraints.notAfterDate ||
      !validityChooser.dateConstraints.notAfterRelativeDate ||
      this.isMoreThanMonthsFromNow(selectedValidity.date, validityChooser.dateConstraints.notAfterRelativeDate.runtime)
    ) {
      return null;
    }
    if (validityChooser.dateConstraints.notBeforeDate || validityChooser.dateConstraints.notBeforeRelativeDate) {
      return null;
    }
    if (validityChooser.dateConstraints.allowedDateCronExpression && !/^[*\s]+$/.test(validityChooser.dateConstraints.allowedDateCronExpression)) {
      return null;
    }
    if (!licenseTypeValidity.date) {
      return null;
    }
    return {
      type: 'UPCOMING_MONTHS',
      months: validityChooser.dateConstraints.notAfterRelativeDate.runtime,
      includeCurrentMonth: !(validityChooser.dateConstraints?.enforceDateInFuture ?? false),
      currentDate: new Date(licenseTypeValidity.date)
    };
  }

  private isMoreThanMonthsFromNow(date: Date, numberOfMonths: number): boolean {
    const monthsFromNow: Date = new Date();
    monthsFromNow.setDate(1);
    monthsFromNow.setHours(0, 0, 0, 0);
    monthsFromNow.setMonth(monthsFromNow.getMonth() + numberOfMonths);
    const dateToCompare: Date = new Date(date);
    dateToCompare.setDate(1);
    dateToCompare.setHours(0, 0, 0, 0);
    return dateToCompare.getTime() > monthsFromNow.getTime();
  }

  getSelectedValidityDate(): Date | null {
    const selectedValidity = this.store.selectSnapshot(ShoppingCartState.selectedValidity);
    if (selectedValidity && selectedValidity.date) {
      if (selectedValidity.granularity === 'MONTH') {
        return new Date(Date.UTC(selectedValidity.date.getUTCFullYear(), selectedValidity.date.getUTCMonth(), 1, 0, 0, 0, 0));
      }
      if (selectedValidity.granularity === 'DATE') {
        return new Date(Date.UTC(selectedValidity.date.getUTCFullYear(), selectedValidity.date.getUTCMonth(), selectedValidity.date.getUTCDate(), 0, 0, 0, 0));
      }
      return new Date(
        Date.UTC(
          selectedValidity.date.getUTCFullYear(),
          selectedValidity.date.getUTCMonth(),
          selectedValidity.date.getUTCDate(),
          selectedValidity.date.getUTCHours(),
          selectedValidity.date.getUTCMinutes(),
          selectedValidity.date.getUTCSeconds(),
          selectedValidity.date.getUTCMilliseconds()
        )
      );
    }
    return null;
  }
}
